import {
  ComponentType,
  createElement,
  lazy,
  LazyExoticComponent,
  ReactElement,
} from 'react';

export const CHUNK_LOAD_ERROR_NAME = 'ChunkLoadError';

const CHUNK_LOAD_ERROR_PATTERN =
  /dynamically imported module|importing a module script failed|failed to fetch|load failed|networkerror/i;
const FAILED_MODULE_URL_PATTERN = /(https?:\/\/[^\s'"]+)/;
const RETRY_PARAM = 'retry';

type ModuleNamespace = Record<string, unknown>;

type ImportWithRetryOptions<T> = {
  retries?: number;
  baseDelayMs?: number;
  isExpectedModule?: (module: T) => boolean;
  importModule?: (url: string) => Promise<unknown>;
};

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const importModuleByUrl = (url: string) => import(/* @vite-ignore */ url);

const hasDefaultExport = (module: unknown) =>
  typeof module === 'object' && module !== null && 'default' in module;

const isModuleNamespace = (value: unknown): value is ModuleNamespace =>
  typeof value === 'object' &&
  value !== null &&
  (value as {[Symbol.toStringTag]?: unknown})[Symbol.toStringTag] === 'Module';

export const isChunkLoadError = (error: unknown): error is Error =>
  error instanceof Error &&
  (error.name === CHUNK_LOAD_ERROR_NAME || CHUNK_LOAD_ERROR_PATTERN.test(error.message));

const getFailedModuleUrl = (error: Error) => {
  const url = error.message.match(FAILED_MODULE_URL_PATTERN)?.[1];
  if (!url) return null;

  try {
    return new URL(url);
  } catch {
    return null;
  }
};

const resolveReimportedModule = <T>(
  namespace: unknown,
  isExpectedModule: (module: T) => boolean,
) => {
  if (!isModuleNamespace(namespace)) return null;

  const candidates = [
    namespace,
    ...Object.values(namespace).filter(isModuleNamespace),
  ].filter(candidate => isExpectedModule(candidate as T));

  return candidates.length === 1 ? (candidates[0] as T) : null;
};

const toChunkLoadError = (error: Error) => {
  if (error.name === CHUNK_LOAD_ERROR_NAME) return error;

  const chunkLoadError = new Error(error.message);
  chunkLoadError.name = CHUNK_LOAD_ERROR_NAME;
  chunkLoadError.stack = error.stack;
  return Object.assign(chunkLoadError, {cause: error});
};

export const importWithRetry = async <T>(
  loader: () => Promise<T>,
  {
    retries = 2,
    baseDelayMs = 1000,
    isExpectedModule = hasDefaultExport,
    importModule = importModuleByUrl,
  }: ImportWithRetryOptions<T> = {},
): Promise<T> => {
  let lastError: unknown;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      const failedModuleUrl =
        attempt > 0 && lastError instanceof Error ? getFailedModuleUrl(lastError) : null;

      if (!failedModuleUrl) {
        return await loader();
      }

      failedModuleUrl.searchParams.set(RETRY_PARAM, `${Date.now()}-${attempt}`);
      const module = resolveReimportedModule(
        await importModule(failedModuleUrl.href),
        isExpectedModule,
      );

      if (!module) break;
      return module;
    } catch (error) {
      if (!isChunkLoadError(error)) throw error;

      lastError = error;

      if (attempt < retries) {
        await wait(baseDelayMs * 2 ** attempt);
      }
    }
  }

  throw toChunkLoadError(lastError as Error);
};

const failedLazyComponentResets = new Set<() => void>();

export const resetFailedLazyComponents = () => {
  failedLazyComponentResets.forEach(reset => reset());
  failedLazyComponentResets.clear();
};

type LazyWithRetryComponent<P extends object> = (props: P) => ReactElement;

export function lazyWithRetry<P extends object>(
  loader: () => Promise<{default: ComponentType<P>}>,
): LazyWithRetryComponent<P>;
export function lazyWithRetry<M, P extends object>(
  loader: () => Promise<M>,
  pickComponent: (module: M) => ComponentType<P>,
): LazyWithRetryComponent<P>;

export function lazyWithRetry<M, P extends object>(
  loader: () => Promise<M>,
  pickComponent: (module: M) => ComponentType<P> = module =>
    (module as {default: ComponentType<P>}).default,
): LazyWithRetryComponent<P> {
  const createLazyComponent = (): LazyExoticComponent<ComponentType<P>> =>
    lazy(() =>
      importWithRetry(loader, {
        isExpectedModule: module => Boolean(pickComponent(module)),
      })
        .then(module => ({default: pickComponent(module)}))
        .catch(error => {
          failedLazyComponentResets.add(() => {
            currentLazyComponent = createLazyComponent();
          });
          throw error;
        }),
    );

  let currentLazyComponent = createLazyComponent();

  return props =>
    createElement(currentLazyComponent as unknown as ComponentType<P>, props);
}
